import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { Pagination } from 'react-instantsearch';

import styles from './Pagination.module.scss';

/**
 * Search pagination component.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/pagination/react/
 */

const SearchPagination = () => {
  const t = useTranslations('search');

  return (
    <Pagination
      showFirst={false}
      showLast={false}
      translations={{
        firstPageItemText: t('pagination.firstPageItemText'),
        previousPageItemText: '',
        nextPageItemText: '',
        lastPageItemText: t('pagination.lastPageItemText'),
        pageItemText: ({ currentPage }) => currentPage.toString(),
        firstPageItemAriaLabel: t('pagination.firstPageItemAriaLabel'),
        previousPageItemAriaLabel: t('pagination.previousPageItemAriaLabel'),
        nextPageItemAriaLabel: t('pagination.nextPageItemAriaLabel'),
        lastPageItemAriaLabel: t('pagination.lastPageItemAriaLabel'),
        pageItemAriaLabel: ({ currentPage, nbPages }) =>
          t('pagination.pageItemAriaLabel', { currentPage, nbPages }),
      }}
      classNames={{
        root: styles.pagination,
        list: styles.list,
        item: styles.item,
        selectedItem: styles.selectedItem,
        disabledItem: styles.disabledItem,
        previousPageItem: styles.previousPageItem,
        nextPageItem: styles.nextPageItem,
        link: styles.link,
      }}
      onClick={handlePaginationClick}
    />
  );
};

/**
 * For some reason, Algolia does not scroll when clicking on the pagination links.
 *
 * This click handler scrolls to the top of the search layout when clicking on a valid pagination link.
 *
 * @param event - The click event of the pagination root element.
 */

const handlePaginationClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  const isPaginationLink = target?.classList.contains(styles.link);

  const targetParent = target.parentElement;
  const isNotSelected = !targetParent?.classList.contains(styles.selectedItem);
  const isNotDisabled = !targetParent?.classList.contains(styles.disabledItem);

  if (isPaginationLink && isNotSelected && isNotDisabled) {
    const element = document.getElementById('search-wrapper');
    element?.scrollIntoView({ behavior: 'smooth' });
  }
};

export default SearchPagination;
